// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { TeamsMeetingLinkLocator } from '@azure/communication-calling';
import { CallAndChatLocator } from '@azure/communication-react';
import { TryMobileApp } from 'components/Drawer/DeepLinkDrawer';
import { StyledSpinner } from 'components/StyledSpinner';
import { useFunctionsAppToken, useUserName } from 'hooks/useUser';
import { useEffect, useState } from 'react';
import { generateTeamsDeepLink, openDeepLinkInNewWindow } from 'utils/generateDeepLink';

/**
 * Page that prompts the user to join via a mobile deep link
 *
 * @remarks
 * Normally we show this options using the DeepLinkDrawer component. However,
 * when the website is visited from a link with meeting details in the URL query params
 * we show this page as an interstitial page where the user can either join the meeting
 * from the mobile app or continue in the browser.
 */
export const MobileInterstitial = (props: {
  deepLinkDetails: { locator?: CallAndChatLocator | TeamsMeetingLinkLocator; continueOnBrowser: () => void };
}): JSX.Element => {
  const [deepLink, setDeepLink] = useState<string>();

  const userName = useUserName();
  const functionsAppToken = useFunctionsAppToken();

  const locator = props.deepLinkDetails.locator;
  useEffect(() => {
    (async () => {
      if (!userName || !functionsAppToken) {
        throw new Error('Cannot generate deep link without user name and meeting details');
      }

      if (locator && 'meetingLink' in locator) {
        setDeepLink(await generateTeamsDeepLink(locator.meetingLink, userName, functionsAppToken));
      } else if (locator && 'groupId' in locator.callLocator) {
        setDeepLink(await generateTeamsDeepLink(locator.callLocator.groupId, userName, functionsAppToken));
      } else {
        console.error('Unsupported locator type for deep links');
      }
    })();
  }, [locator, userName, functionsAppToken]);

  if (props.deepLinkDetails) {
    return (
      <TryMobileApp
        onContinueInBrowser={props.deepLinkDetails.continueOnBrowser}
        onDeepLinkClick={() => {
          if (!deepLink) {
            alert('Could not generate a deep link');
            return;
          }
          openDeepLinkInNewWindow(deepLink);
        }}
      />
    );
  }

  return <StyledSpinner label="Setting up call..." />;
};
